/**
 * SEO HEADING STRUCTURE - Homepage (Index)
 * ========================================
 * 
 * TITLE: CSW - Study Stream | Study With Me | Body Doubling | Study Together
 * H1: "Study Together. Focus Together." (Hero)
 * H1: "Study Stream" (StudyStream)
 * H1: "Study With Me" (StudyWithMeSection)
 * H2: "Body Doubling: The Science Behind It" (BodyDoubling)
 * H2: "Ready to Focus Together?" (CTA)
 */

import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Hero } from "@/components/Hero";
import { StudyStream } from "@/components/StudyStream";
import { BodyDoubling } from "@/components/BodyDoubling";
import { StudyWithMeSection } from "@/components/StudyWithMeSection";
import { StudyTogetherSection } from "@/components/StudyTogetherSection";
import { CTA } from "@/components/CTA";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";

const homepageSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": "https://csw.live/#webpage",
      "url": "https://csw.live/",
      "name": "CSW - Study Stream | Study With Me | Body Doubling | Study Together Online",
      "description": "Free virtual study rooms powered by body doubling. Join study streams, study with me sessions, and study together with a global community. Stay focused with visual accountability.",
      "inLanguage": "en",
      "dateModified": "2026-03-22",
      "isPartOf": {
        "@type": "WebSite",
        "@id": "https://csw.live/#website",
        "url": "https://csw.live/",
        "name": "CSW",
        "publisher": { "@type": "Organization", "name": "CSW", "url": "https://csw.live" }
      },
      "about": {
        "@type": "SoftwareApplication",
        "name": "CSW",
        "applicationCategory": "ProductivityApplication",
        "operatingSystem": "Web Browser",
        "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "5", "ratingCount": "3000", "bestRating": "5", "worstRating": "1" }
      },
      "keywords": [
        "study stream", "study with me", "body doubling", "study together",
        "virtual study room", "study with strangers", "online study room",
        "social accountability", "focus rooms", "free study app"
      ]
    },
    {
      "@type": "SoftwareApplication",
      "@id": "https://csw.live/#software",
      "name": "CSW - Chill Study Work",
      "applicationCategory": "ProductivityApplication",
      "operatingSystem": "Web Browser",
      "description": "Free body doubling platform with study streams, study with me sessions, and virtual study rooms. Study together with a global community.",
      "url": "https://csw.live",
      "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD", "availability": "https://schema.org/InStock" },
      "aggregateRating": { "@type": "AggregateRating", "ratingValue": "5", "ratingCount": "3000", "bestRating": "5", "worstRating": "1" }
    },
    {
      "@type": "Organization",
      "@id": "https://csw.live/#organization",
      "name": "CSW",
      "url": "https://csw.live",
      "logo": "https://csw.live/favicon.png"
    },
    {
      "@type": "BreadcrumbList",
      "@id": "https://csw.live/#breadcrumb",
      "itemListElement": [{ "@type": "ListItem", "position": 1, "name": "Home", "item": "https://csw.live/" }]
    }
  ]
};

const Index = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="CSW - Study Stream | Study With Me | Body Doubling | Study Together"
        description="Free virtual study rooms powered by body doubling. Join study streams and stay focused with a global community."
        keywords="study stream, study with me, body doubling, study together, virtual study room, study with strangers, social accountability, focus rooms"
        canonicalUrl="/"
        isHomepage={true}
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(homepageSchema)}
        </script>
      </Helmet>
      <Header />
      <main>
        <Hero />
        <StudyStream />
        <StudyWithMeSection />
        <BodyDoubling />
        <StudyTogetherSection />
        <CTA />
      </main>
      <Footer />
    </div>
  );
};

export default Index;
